import { useAdmin } from '../../contexts/AdminContext';

/**
 * SiteManagerGate — route-level wrapper that blocks access to site-manager views.
 * Renders children when isSiteManager === true OR isAdmin === true (admins can debug).
 * Otherwise shows a sign-in prompt (Google only) or a "not authorized" card.
 * Visual style: Departure Board / Honest Industrial.
 */
export default function SiteManagerGate({ children }) {
  const { isAdmin, isSiteManager, user, signingIn, signIn, signOut } = useAdmin();

  // Admins bypass — they can access any site manager view for debugging.
  if (isAdmin) return children;

  // Site manager: grant access.
  if (isSiteManager) return children;

  // -------------------------------------------------------------------------
  // Shared layout shell
  // -------------------------------------------------------------------------

  const cardWrap = {
    minHeight: '70vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--sp-6)',
    background: 'var(--c-bg)',
  };

  const card = {
    border: '1px solid var(--c-line-hard)',
    background: 'var(--c-bg)',
    padding: '32px 28px',
    maxWidth: 400,
    width: '100%',
    textAlign: 'center',
  };

  const eyebrowStyle = {
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    letterSpacing: '.16em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: 'var(--c-brand)',
    marginBottom: 10,
    display: 'block',
  };

  const headingStyle = {
    fontFamily: "'Geist', sans-serif",
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--c-ink)',
    letterSpacing: '-.01em',
    marginBottom: 12,
  };

  const bodyStyle = {
    fontFamily: "'Geist', sans-serif",
    fontSize: 13,
    color: 'var(--c-ink-3)',
    lineHeight: 1.6,
    marginBottom: 0,
  };

  const divider = {
    height: 1,
    background: 'var(--c-line-soft)',
    margin: '24px 0',
  };

  const btnAmber = {
    width: '100%',
    padding: '9px 0',
    border: 'none',
    background: 'var(--c-brand)',
    color: '#fff',
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'background 120ms ease',
    marginBottom: 8,
  };

  const btnGhost = {
    width: '100%',
    padding: '9px 0',
    background: 'transparent',
    border: '1px solid var(--c-line-soft)',
    color: 'var(--c-ink-2)',
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'border-color 120ms ease, color 120ms ease',
  };

  // -------------------------------------------------------------------------
  // Not signed in — Google sign-in prompt
  // -------------------------------------------------------------------------

  if (!user) {
    return (
      <div style={cardWrap}>
        <div style={card}>
          {/* Lock icon */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <svg
              aria-hidden="true"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--c-ink-3)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="0" ry="0" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>

          <span style={eyebrowStyle}>RESTRICTED</span>

          <h2 style={headingStyle}>Site Manager Access</h2>

          <p style={bodyStyle}>
            Sign in with your <strong style={{ fontWeight: 600, color: 'var(--c-ink)' }}>@ixoragroup.com</strong> or{' '}
            <strong style={{ fontWeight: 600, color: 'var(--c-ink)' }}>@loocafe.com</strong> Google account to access your site view.
          </p>

          <div style={divider} />

          <button
            type="button"
            onClick={signIn}
            disabled={signingIn}
            style={signingIn ? { ...btnAmber, opacity: 0.6, cursor: 'wait' } : btnAmber}
            onMouseEnter={(e) => { if (!signingIn) e.currentTarget.style.background = 'var(--c-brand-2, #C2410C)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--c-brand)'; }}
          >
            {signingIn ? 'SIGNING IN…' : 'SIGN IN WITH GOOGLE'}
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Signed in but not a site manager — not-authorized card
  // -------------------------------------------------------------------------

  return (
    <div style={cardWrap}>
      <div style={card}>
        {/* No-entry icon */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <svg
            aria-hidden="true"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--c-ink-3)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
          </svg>
        </div>

        <span style={eyebrowStyle}>ACCESS DENIED</span>

        <h2 style={headingStyle}>Site Manager Only</h2>

        <p style={bodyStyle}>
          This account doesn't have site manager privileges. Contact your administrator if this is unexpected.
        </p>

        <div style={divider} />

        <button
          type="button"
          onClick={signOut}
          style={btnGhost}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--c-ink-3)'; e.currentTarget.style.color = 'var(--c-ink)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--c-line-soft)'; e.currentTarget.style.color = 'var(--c-ink-2)'; }}
        >
          SIGN OUT
        </button>
      </div>
    </div>
  );
}
